import { Component, type ErrorInfo, type ReactNode } from 'react';

interface ErrorBoundaryProps {
  /** Human-friendly identifier for this boundary, surfaced in the fallback UI. */
  name: string;
  children: ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
  componentStack: string | null;
}

/**
 * Reusable error boundary. Wrap routes that mount heavy third-party renderers
 * (pdfjs-dist, recharts) so an uncaught exception there doesn't blank the whole
 * page — the user keeps the chrome (sidebar, header) and gets a useful stack
 * trace instead of an empty <body>.
 *
 * We only need class components for this; hooks have no equivalent.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { error: null, componentStack: null };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Best-effort console log so the failure also lands in the DevTools console
    // in production builds where React's own warnings are stripped.
    console.error(`[ErrorBoundary:${this.props.name}]`, error, info.componentStack);
    this.setState({ componentStack: info.componentStack ?? null });
  }

  private readonly handleReset = (): void => {
    this.setState({ error: null, componentStack: null });
  };

  override render(): ReactNode {
    if (this.state.error === null) {
      return this.props.children;
    }

    return (
      <div className="card border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
        <strong className="block font-semibold">
          Algo se ha roto en «{this.props.name}».
        </strong>
        <p className="mt-1">{this.state.error.message}</p>
        {this.state.error.stack !== undefined ? (
          <details className="mt-2">
            <summary className="cursor-pointer text-xs text-rose-700">Stack trace</summary>
            <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap break-all text-xs text-rose-900">
              {this.state.error.stack}
              {this.state.componentStack !== null ? `\n\nComponent stack:${this.state.componentStack}` : ''}
            </pre>
          </details>
        ) : null}
        <button
          type="button"
          className="btn-secondary mt-3"
          onClick={this.handleReset}
        >
          Reintentar
        </button>
      </div>
    );
  }
}
